import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiCode, FiSmartphone, FiGlobe, FiDatabase, FiActivity, FiCpu } from 'react-icons/fi';

const Projects = ({ hideTitle = false }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const projects = [
    {
      title: 'ExactFlow',
      description: 'Multi-marketplace e-commerce platform handling product listings, stock synchronization, order processing, invoicing, and warehouse management system (WMS) workflows at Exact Solution Electronics.',
      category: 'E-commerce',
      technologies: ['Manual Testing', 'Functional Testing', 'Regression Testing', 'Integration Testing', 'WMS'],
      features: [
        'Validated product, order, shipment, WMS, picking, packing, stocktake, inventory reconciliation, and offer listing workflows across interconnected modules',
        'Verified stock synchronization, product associations, shipment tracking, and order status updates',
        'Performed regression and edge-case testing to ensure platform stability and data integrity',
      ],
      github: '',
      demo: '',
      icon: FiGlobe
    },
    {
      title: 'RIS / LAB',
      description: 'Radiology Information System — Healthcare application for patient registration, role-based access control, PACS workflows, and reporting at BugVerse.',
      category: 'Healthcare',
      technologies: ['Cypress', 'SQL', 'RBAC', 'Compliance Testing'],
      features: [
        'Automated and validated patient registration, RBAC, PACS workflows, and reporting',
        'Performed SQL-based data validation and compliance-oriented testing',
      ],
      github: '',
      demo: '',
      icon: FiActivity
    },
    {
      title: 'Creative AI Agents Marketplace',
      description: 'AI-agent marketplace platform for text, audio, and image generation pipelines with role-based access and credit management at BugVerse.',
      category: 'AI/Automation',
      technologies: ['Cypress', 'JavaScript', 'AI Agents', 'RBAC'],
      features: [
        'Validated AI-agent workflows, role-based access, onboarding, and credit management',
        'Ensured accurate execution of text, audio, and image generation pipelines',
      ],
      github: '',
      demo: '',
      icon: FiCpu
    },
    {
      title: 'My AI Front Desk',
      description: 'AI-powered virtual receptionist platform for appointment scheduling and automated client communication at UltraCodes.',
      category: 'AI/Automation',
      technologies: ['Cypress', 'JavaScript', 'Real-time Testing', 'Automation'],
      features: [
        'Automated Cypress test coverage for chat responses, calendar integrations, and call handling workflows',
        'Performed end-to-end testing for real-time communication features and validated AI-driven behaviors',
      ],
      github: '',
      demo: '',
      icon: FiSmartphone
    },
    {
      title: 'Product Monitor',
      description: 'Real-time monitoring platform for product listings and competitive price tracking across e-commerce platforms at UltraCodes.',
      category: 'E-commerce',
      technologies: ['Cypress', 'JavaScript', 'E-commerce', 'UI Testing'],
      features: [
        'Validated real-time product monitoring workflows; ensured UI accuracy and data integrity across dashboards',
        'Automated regression checks using Cypress for critical monitoring flows and data refresh behavior',
      ],
      github: '',
      demo: '',
      icon: FiDatabase
    },
    {
      title: 'Trailer Central',
      description: 'SaaS for Dealership Inventory & CRM — comprehensive software and marketing solution for trailer dealerships at UltraCodes.',
      category: 'CRM/Inventory',
      technologies: ['Manual Testing', 'Functional', 'Regression', 'Integration'],
      features: [
        'Executed functional and regression testing for inventory management and CRM modules across releases',
        'Validated integrations and key workflows to ensure stability in staging and production environments',
      ],
      github: '',
      demo: '',
      icon: FiGlobe
    },
    {
      title: 'Trailer Trader',
      description: 'Largest SaaS-based online marketplace in the US connecting buyers and sellers of recreational and commercial trailers.',
      category: 'Marketplace',
      technologies: ['Manual Testing', 'Agile', 'Cross-platform'],
      features: [
        'Manual testing for transaction processing and user verification',
        'Cross-platform testing for web and mobile',
      ],
      github: '',
      demo: '',
      icon: FiGlobe
    },
    {
      title: 'Transax',
      description: 'Quality assurance for trailer dealership online sales platform with integrated payment systems.',
      category: 'Payments',
      technologies: ['Manual Testing', 'QA'],
      features: [
        'QA for online sales and payment integration',
      ],
      github: '',
      demo: '',
      icon: FiCode
    },
    {
      title: 'Keyki',
      description: 'E-commerce platform testing for millions of products with discount management system.',
      category: 'E-commerce',
      technologies: ['Manual Testing', 'E-commerce'],
      features: [
        'Testing for discount management and product workflows',
      ],
      github: '',
      demo: '',
      icon: FiCode
    },
    {
      title: 'Xanalia',
      description: 'Blockchain-based NFT marketplace testing on BSC and Polygon chains.',
      category: 'Blockchain',
      technologies: ['Manual Testing', 'Blockchain', 'NFT'],
      features: [
        'Testing for NFT minting, trading, and blockchain integration',
      ],
      github: '',
      demo: '',
      icon: FiCode
    },
    {
      title: 'Almana Hospital',
      description: 'Mobile application testing for medical records and appointment booking features.',
      category: 'Healthcare',
      technologies: ['Manual Testing', 'Mobile', 'Healthcare'],
      features: [
        'Testing for medical records and appointment booking',
      ],
      github: '',
      demo: '',
      icon: FiSmartphone
    },
  ];

  return (
    <section id="projects" ref={ref} className="section-padding bg-gradient-to-br from-secondary-50 to-primary-50 dark:from-dark-50 dark:to-dark-100 transition-colors duration-300">
      <div className="container-custom">
        {!hideTitle && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-secondary-800 dark:text-white mb-4">
            Key <span className="gradient-text">Projects</span>
          </h2>
          <p className="text-lg text-secondary-600 dark:text-gray-100 max-w-2xl mx-auto">
            A selection of my recent SQA and automation projects, including AI, e-commerce, CRM, blockchain, and healthcare platforms.
          </p>
        </motion.div>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={project.title}
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ delay: index * 0.2 }}
              className="relative bg-white/80 dark:bg-dark-200/80 border border-primary-100 dark:border-dark-300 rounded-2xl shadow-lg p-6 flex flex-col"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="text-xs font-semibold px-3 py-1 rounded-full bg-primary-100 dark:bg-primary-700 text-primary-700 dark:text-primary-200">
                  {project.category}
                </span>
                {project.icon && <project.icon className="w-6 h-6 text-primary-500 dark:text-primary-300 absolute top-6 right-6" />}
              </div>
              <h3 className="text-xl font-bold mb-2 text-secondary-800 dark:text-white">{project.title}</h3>
              <p className="text-base text-secondary-600 dark:text-gray-100 mb-4 flex-1">{project.description}</p>
              <div className="mb-4">
                <h4 className="font-semibold text-secondary-700 dark:text-gray-100 mb-2">Key Features:</h4>
                <ul className="list-disc list-inside space-y-1 text-secondary-600 dark:text-gray-100">
                  {project.features.map((feature, i) => (
                    <li key={i}>{feature}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-secondary-700 dark:text-gray-100 mb-2">Technologies:</h4>
                <div className="flex flex-wrap gap-2">
                  {project.technologies.map((tech, i) => (
                    <span key={i} className="px-3 py-1 bg-primary-100 dark:bg-primary-700 text-primary-700 dark:text-primary-200 text-xs font-medium rounded-full border border-primary-200 dark:border-primary-400">
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects; 